import React from 'react';

// Componente base Skeleton
export const Skeleton = ({ className = '', width, height, variant = 'rectangular' }) => {
  const baseClasses = 'animate-pulse bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%]';
  const variantClasses = {
    rectangular: 'rounded',
    circular: 'rounded-full',
    text: 'rounded h-4'
  };

  const style = {
    width: width || '100%',
    height: height || (variant === 'text' ? '1rem' : '100%')
  };

  return (
    <div 
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={style}
    />
  );
};

// Skeleton para Cards
export const SkeletonCard = ({ className = '' }) => (
  <div className={`bg-white dark:bg-gray-800 rounded-lg p-6 ${className}`}>
    <Skeleton className="mb-4" height="24px" width="60%" />
    <Skeleton className="mb-2" height="16px" width="100%" />
    <Skeleton className="mb-2" height="16px" width="90%" />
    <Skeleton height="16px" width="70%" />
  </div>
);

// Skeleton para Tabela
export const SkeletonTable = ({ rows = 5, columns = 4 }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
    {/* Header */}
    <div className="border-b border-gray-200 dark:border-gray-700 p-4">
      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
        {Array(columns).fill(0).map((_, i) => (
          <Skeleton key={i} height="20px" />
        ))}
      </div>
    </div>
    {/* Rows */}
    {Array(rows).fill(0).map((_, rowIndex) => (
      <div key={rowIndex} className="border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
          {Array(columns).fill(0).map((_, colIndex) => (
            <Skeleton key={colIndex} height="16px" />
          ))}
        </div>
      </div>
    ))}
  </div>
);

// Skeleton para Calendário
export const SkeletonCalendar = () => (
  <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
    {/* Header do calendário */}
    <div className="flex justify-between items-center mb-6">
      <Skeleton width="150px" height="32px" />
      <div className="flex gap-2">
        <Skeleton width="40px" height="40px" />
        <Skeleton width="40px" height="40px" />
      </div>
    </div>
    
    {/* Grid de dias da semana */}
    <div className="grid grid-cols-7 gap-2 mb-4">
      {Array(7).fill(0).map((_, i) => (
        <Skeleton key={i} height="40px" />
      ))}
    </div>
    
    {/* Grid de dias do mês */}
    <div className="grid grid-cols-7 gap-2">
      {Array(35).fill(0).map((_, i) => (
        <Skeleton key={i} height="100px" />
      ))}
    </div>
  </div>
);

// Skeleton para Lista de Clientes
export const SkeletonClientList = ({ count = 5 }) => (
  <div className="space-y-3">
    {Array(count).fill(0).map((_, i) => (
      <div key={i} className="bg-white dark:bg-gray-800 rounded-lg p-4 flex items-center gap-4">
        <Skeleton variant="circular" width="48px" height="48px" />
        <div className="flex-1">
          <Skeleton className="mb-2" width="40%" height="20px" />
          <Skeleton width="60%" height="16px" />
        </div>
        <Skeleton width="80px" height="32px" />
      </div>
    ))}
  </div>
);

// Skeleton para Gráficos
export const SkeletonChart = ({ className = '' }) => (
  <div className={`bg-white dark:bg-gray-800 rounded-lg p-6 ${className}`}>
    <Skeleton className="mb-6" width="40%" height="24px" />
    <div className="flex items-end justify-between h-64 gap-4">
      {Array(7).fill(0).map((_, i) => (
        <Skeleton 
          key={i} 
          className="flex-1" 
          height={`${Math.random() * 60 + 40}%`} 
        />
      ))}
    </div>
  </div>
);

// Skeleton para Dashboard Stats
export const SkeletonStats = ({ count = 4 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
    {Array(count).fill(0).map((_, i) => (
      <div key={i} className="bg-white dark:bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <Skeleton width="60%" height="16px" />
          <Skeleton variant="circular" width="40px" height="40px" />
        </div>
        <Skeleton width="80%" height="32px" />
      </div>
    ))}
  </div>
);

// Skeleton para Formulário
export const SkeletonForm = ({ fields = 4 }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg p-6">
    <Skeleton className="mb-6" width="50%" height="28px" />
    <div className="space-y-4">
      {Array(fields).fill(0).map((_, i) => (
        <div key={i}>
          <Skeleton className="mb-2" width="30%" height="16px" />
          <Skeleton width="100%" height="40px" />
        </div>
      ))}
    </div>
    <div className="flex gap-3 mt-6">
      <Skeleton width="100px" height="40px" />
      <Skeleton width="100px" height="40px" />
    </div>
  </div>
);

// Loading Spinner com animação suave
export const LoadingSpinner = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div className={`${sizes[size]} border-4 border-gray-200 dark:border-gray-700 border-t-pink-500 rounded-full animate-spin`} />
    </div>
  );
};

// Loading Overlay
export const LoadingOverlay = ({ message = 'A carregar...', show = true }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity duration-300">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-8 flex flex-col items-center gap-4 shadow-xl">
        <LoadingSpinner size="lg" />
        <p className="text-gray-700 dark:text-gray-300 font-medium">{message}</p>
      </div>
    </div>
  );
};

export default Skeleton;
